/**
 * The backoffice event stream (10-web-admin § 2.2 step 4, 04 § 8.6).
 *
 * Started once the gate has a role, stopped on sign-out. Everything it does is
 * invalidate query keys: the server sends no state a screen could apply
 * directly, and re-reading is the only way to stay correct after a reconnect,
 * which the server does not replay across.
 */

import type { QueryClient } from '@tanstack/react-query';
import { createSseClient, invalidationFor, RECONNECT_INVALIDATIONS, type SseClient } from '@cowoo/shared';
import { env } from '../env';
import { useAdminStore } from './store';

let client: SseClient | null = null;

type Listener = (data: unknown) => void;
const listeners = new Map<string, Set<Listener>>();

/**
 * Watch one event type, for the screens that need the payload and not just a
 * refetch (§ 15: the remote open waits for *its* `terminal_ack` result).
 *
 * Returns the unsubscribe, so a screen that unmounts stops listening — otherwise
 * the next ack would resolve a wait nobody is looking at.
 */
export function onAdminEvent(event: string, listener: Listener): () => void {
  const set = listeners.get(event) ?? new Set<Listener>();
  set.add(listener);
  listeners.set(event, set);
  return () => set.delete(listener);
}

export function startSse(queryClient: QueryClient): void {
  if (client) return;

  const invalidate = (keys: string[][]): void => {
    for (const key of keys) void queryClient.invalidateQueries({ queryKey: key });
  };

  client = createSseClient({
    url: `${env.API_BASE_URL}/v1/events`,
    getToken: () => useAdminStore.getState().token,
    onEvent: (message) => {
      invalidate(invalidationFor(message.event, message.data));
      for (const listener of listeners.get(message.event) ?? []) listener(message.data);
    },
    onStateChange: (state) => {
      const previous = useAdminStore.getState().sse;
      useAdminStore.getState().setSse(state);
      // A stream that was down and is up again missed everything in between.
      if (state === 'open' && previous !== 'connecting') {
        invalidate([...RECONNECT_INVALIDATIONS, ['admin']]);
      }
    },
    onUnauthorized: () => useAdminStore.getState().clear(),
  });
  client.start();
}

export function stopSse(): void {
  client?.stop();
  client = null;
}
